import React from "react";
import { Box, Stack, Container, Typography } from "@mui/material";
import { Link } from "react-router-dom";

import Logo from "../../assets/datalumio webp/footerlumio.webp";
import FacebookIconImg from "../../assets/datalumio webp/Facebook.webp";
import LinkedInIconImg from "../../assets/datalumio webp/LinkedIn.webp";
import XIconImg from "../../assets/datalumio webp/X.webp";

// ── icons ─────────────────────────────────────────
const LinkedInIcon = () => (
  <img
    src={LinkedInIconImg}
    alt=""
    aria-hidden="true"
    style={{
      width: "18px",
      height: "18px",
      objectFit: "contain",
    }}
  />
);

const XIcon = () => (
  <img
    src={XIconImg}
    alt=""
    aria-hidden="true"
    style={{
      width: "16px",
      height: "16px",
      objectFit: "contain",
    }}
  />
);

const FacebookIcon = () => (
  <img
    src={FacebookIconImg}
    alt=""
    aria-hidden="true"
    style={{
      width: "18px",
      height: "18px",
      objectFit: "contain",
    }}
  />
);

// ── constants ─────────────────────────────────────
const BG = "#0B1739";

const ProductHuntBadge = () => (
  // Decorative: the wrapping <a> carries the accessible name. SVG <text> does
  // not contribute to a link's accessible name, so it must not be relied on.
  <svg width="250" height="54" viewBox="0 0 250 54" version="1.1" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g transform="translate(-130.000000, -73.000000)">
        <g transform="translate(130.000000, 73.000000)">
          <rect stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="transparent" x="0.5" y="0.5" width="249" height="53" rx="10"></rect>
          <text fontFamily="Helvetica-Bold, Helvetica" fontSize="9" fontWeight="bold" fill="#FFFFFF">
            <tspan x="53" y="20">LEAVE A REVIEW ON</tspan>
          </text>
          <text fontFamily="Helvetica-Bold, Helvetica" fontSize="16" fontWeight="bold" fill="#FFFFFF">
            <tspan x="52" y="40">Product Hunt</tspan>
          </text>
          <g transform="translate(205.000000, 11.000000) scale(1.4)" fill="#FFFFFF">
            <g transform="translate(3, 0)">
              <path d="M23.04,9.021L14.77,8.796L12,1L9.23,8.796L0.96,9.021l6.559,5.043L5.177,22L12,17.321L18.823,22l-2.342-7.935L23.04,9.021z M12,14.896l-3.312,2.271l1.137-3.851l-3.183-2.448l4.014-0.109L12,6.974l1.344,3.784l4.014,0.109l-3.183,2.448l1.137,3.851 L12,14.896z"></path>
            </g>
          </g>
          <g transform="translate(11.000000, 12.000000)">
            <path d="M31,15.5 C31,24.0603917 24.0603917,31 15.5,31 C6.93960833,31 0,24.0603917 0,15.5 C0,6.93960833 6.93960833,0 15.5,0 C24.0603917,0 31,6.93960833 31,15.5" fill="#FFFFFF"></path>
            <path d="M17.4329412,15.9558824 L17.4329412,15.9560115 L13.0929412,15.9560115 L13.0929412,11.3060115 L17.4329412,11.3060115 L17.4329412,11.3058824 C18.7018806,11.3058824 19.7305882,12.3468365 19.7305882,13.6308824 C19.7305882,14.9149282 18.7018806,15.9558824 17.4329412,15.9558824 M17.4329412,8.20588235 L17.4329412,8.20601152 L10.0294118,8.20588235 L10.0294118,23.7058824 L13.0929412,23.7058824 L13.0929412,19.0560115 L17.4329412,19.0560115 L17.4329412,19.0558824 C20.3938424,19.0558824 22.7941176,16.6270324 22.7941176,13.6308824 C22.7941176,10.6347324 20.3938424,8.20588235 17.4329412,8.20588235" fill={BG}></path>
          </g>
        </g>
      </g>
    </g>
  </svg>
);
const FONT = "'Poppins', sans-serif";
const BLUE = "#4B7BF5";
const MUTED = "#C8D4E8";
const ICON_BG = "#1B2C5A";

const styles = {
  link: {
    color: MUTED,
    fontSize: 18,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: FONT,
    transition: "color .2s ease",
    textDecoration: "none",
    display: "block",
  },

  socialRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    textDecoration: "none",
    cursor: "pointer",
    position: "relative",
  },

  iconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    background: ICON_BG,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  socialLabel: {
    color: MUTED,
    fontSize: 18,
    fontWeight: 500,
    fontFamily: FONT,
  },
};

// ── hover link ────────────────────────────────────
const HoverLink = ({ href, target, children, style }) => {
  const [hovered, setHovered] = React.useState(false);

  const isExternal = href.startsWith("http");

  return isExternal ? (
    <a
      href={href}
      target={target || "_blank"}
      rel="noreferrer"
      style={{
        ...styles.link,
        ...style,
        color: hovered ? "#fff" : MUTED,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </a>
  ) : (
    <Link
      to={href}
      style={{
        ...styles.link,
        ...style,
        color: hovered ? "#fff" : MUTED,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </Link>
  );
};

// ── social ────────────────────────────────────────
const HoverSocial = ({ href, icon, label, isMobile }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{
        ...styles.socialRow,
        gap: isMobile ? 16 : 12,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={isMobile ? label : ""}
    >
      <span
        style={{
          ...styles.iconBox,
          width: isMobile ? 42 : 32,
          height: isMobile ? 42 : 32,
        }}
      >
        {icon}
      </span>

      {!isMobile && (
        <span
          style={{
            ...styles.socialLabel,
            color: hovered ? "#fff" : MUTED,
          }}
        >
          {label}
        </span>
      )}

      {isMobile && hovered && (
        <span
          style={{
            position: "absolute",
            bottom: "-30px",
            background: ICON_BG,
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            whiteSpace: "nowrap",
            zIndex: 10,
            fontFamily: FONT,
          }}
        >
          {label}
        </span>
      )}
    </a>
  );
};

// ── footer ────────────────────────────────────────
const Footer = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 960);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 960);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      component="footer"
      sx={{
        background: BG,
        pt: { xs: 8, md: 10 },
        pb: { xs: 6, md: 8 },
        px: { xs: 3, md: 6 },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2,1fr)",
              md: "1.6fr 1fr 1fr 1fr 1fr",
            },
            gap: { xs: 6, md: 4 },
          }}
        >
          {/* Brand */}
          <Box
            sx={{
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Link to="/">
              <img
                src={Logo}
                alt="DataLumio"
                width={160}
                style={{
                  width: 160,
                  cursor: "pointer",
                  display: isMobile ? "block" : "inline-block",
                  margin: isMobile ? "0 auto" : "0",
                }}
              />
            </Link>

            <Typography
              sx={{
                color: MUTED,
                mt: 3,
                lineHeight: 1.8,

                "&:hover": {
                  color: "#fff",
                },
              }}
            >
              Turn messy spreadsheets, PDFs, and research files into
              clear insights with AI-powered analysis. DataLumio helps
              you clean, analyze, visualize, and report data faster in
              one simple workspace.
            </Typography>
            <Box sx={{ mt: 4 }}>
              <a 
                href="https://www.producthunt.com/products/datalumio/reviews/new?utm_source=badge-product_review&utm_medium=badge&utm_source=badge-datalumio"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Leave a review for DataLumio on Product Hunt (opens in a new tab)"
              >
                <ProductHuntBadge />
              </a>
            </Box>
          </Box>

          {/* Links */}
          <Box
            sx={{
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography
              sx={{
                color: BLUE,
                fontWeight: 700,
                mb: 4,
              }}
            >
              Quick Links
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(2,1fr)" },
                gap: 2,
              }}
            >
              <HoverLink href="/">Home</HoverLink>
              <HoverLink href="/about">About</HoverLink>
              <HoverLink href="/features">Features</HoverLink>
              <HoverLink href="/pricing">Pricing</HoverLink>
              <HoverLink href="/contact-us">Contact</HoverLink>
              <HoverLink href="/demo">Demo</HoverLink>
              <HoverLink href="/blogs">Blog</HoverLink>
            </Box>
          </Box>
          {/* Products */}
          <Box>
            <Typography
              sx={{
                color: BLUE,
                fontWeight: 700,
                mb: 4,
              }}
            >
              Products
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2,1fr)", md: "1fr" },
                gap: 2,
              }}
            >
              <HoverLink href="/ai-agent">
                AI Agent
              </HoverLink>
              <HoverLink href="/features/qualitative-data-analysis">
                Qualitative Analysis
              </HoverLink>
              <HoverLink href="/features/quantitative-data-analysis">
                Quantitative Analysis
              </HoverLink>
              <HoverLink href="/features/data-cleaning">
                Data Cleaning
              </HoverLink>
              <HoverLink href="/features/data-visualization-dashboard">
                Data Dashboard
              </HoverLink>
              <HoverLink href="/features/data-integration">
                Data Integration
              </HoverLink>
              <HoverLink href="/features/pdf-analysis">
                PDF Analysis
              </HoverLink>
            </Box>
          </Box>
          {/* Social */}
          <Box>
            <Typography
              sx={{
                color: BLUE,
                fontWeight: 700,
                mb: 4,
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Social
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", md: "column" },
                gap: { xs: 1, md: 2 },
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <HoverSocial
                href="https://www.linkedin.com/company/datalumio/about/"
                icon={<LinkedInIcon />}
                label="LinkedIn"
                isMobile={isMobile}
              />

              <HoverSocial
                href="https://x.com/DataLumio"
                icon={<XIcon />}
                label="X.com"
                isMobile={isMobile}
              />

              <HoverSocial
                href="https://www.facebook.com/profile.php?id=61577569461059"
                icon={<FacebookIcon />}
                label="Facebook"
                isMobile={isMobile}
              />
            </Box>
          </Box>

          {/* Contact */}
          <Box>
            <Typography
              sx={{
                color: BLUE,
                fontWeight: 700,
                mb: 4,
              }}
            >
              Contact
            </Typography>

            <Typography
              sx={{
                color: MUTED,
                mb: 2,
              }}
            >
              Email:{" "}

              <a
                href="mailto:info@datalumio.co"
                style={{
                  color: MUTED,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Info@datalumio.co
              </a>
            </Typography>

            <Typography
              sx={{
                color: MUTED,
                lineHeight: 1.7,
              }}
            >
              Level 1, Devonshire House, One
              <br />
              Mayfair Place, London, UK
            </Typography>
          </Box>
        </Box>

        {/* Bottom */}
        <Box
          sx={{
            borderTop: "1px solid #1B2C5A",
            mt: 8,
            pt: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              flexWrap: "wrap",
              mb: 3,
            }}
          >
            {[
              ["Privacy Policy", "/privacy-policy"],
              ["Terms & Conditions", "/terms-and-conditions"],
              ["Cookie Policy", "/cookie-policy"],
              ["AI & Data Usage Policy", "/ai-data-usage-policy"],
            ].map(([label, to], i) => (
              <React.Fragment key={to}>
                <Link
                  to={to}
                  style={{
                    color: MUTED,
                    textDecoration: "none",
                    fontSize: 13,
                  }}
                >
                  {label}
                </Link>

                {i < 3 && (
                  <Typography
                    sx={{
                      color: "#1B2C5A",
                    }}
                  >
                    |
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: "#94A3B8", textAlign: "center" }}>
              © 2026{" "}
              <Link
                to="/"
                style={{
                  color: "#94A3B8",
                  textDecoration: "underline",
                  fontWeight: 500,
                }}
              >
                DataLumio
              </Link>{" "}
              All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
